import {
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import EventIcon from "@mui/icons-material/Event";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import TableContainer from "../../Components/TableContainer";

// Events manager — event check-in rewards (event-checkin-rewards-plan.md).
//
// An event is one shared code valid in a window. Redeeming it once per
// account grants a medal (to the dogs the user picks), an event-exclusive
// Doggo item, and TreatPoints. Art lives in the Badge Manager (criteria
// "event_checkin", premium OFF, square PNG ≥ 512) and the Closet Manager
// (acquisition "Event exclusive", exact-canvas layer + thumb). This screen
// only picks from those and sets code / window / cap / points.
//
// Window times are entered in the browser's local time and stored UTC.
// Pad the window: open the day before, keep it open ~2 weeks after so
// late installs still get their rewards.

const EMPTY_FORM = {
    id: null,
    code: "",
    name: "",
    partner: "",
    venue: "",
    description: "",
    event_date: "",
    starts_at: "",
    ends_at: "",
    max_redemptions: "",
    badge_id: "",
    cosmetic_id: "",
    tp_amount: 0,
};

const STATUS_META = {
    1: { label: "live", bg: "#c6ff0060" },
    0: { label: "draft", bg: "#e0e0e0" },
    "-1": { label: "archived", bg: "#ffcdd2" },
};

// "YYYY-MM-DDTHH:MM" (datetime-local) → ISO UTC string for the API.
const localToIso = (v) => (v ? new Date(v).toISOString() : "");
// UTC datetime from the API → "YYYY-MM-DDTHH:MM" in the browser's zone.
const isoToLocal = (v) => {
    if (!v) return "";
    const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : `${String(v).replace(" ", "T")}Z`);
    if (isNaN(d.getTime())) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fmtLocal = (v) => {
    if (!v) return "—";
    const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : `${String(v).replace(" ", "T")}Z`);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const EventManager = () => {
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [badges, setBadges] = useState([]);
    const [cosmetics, setCosmetics] = useState([]);
    const [page, setPage] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const pageSize = 25;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await AxiosInstance.get(ApiList.ADMIN_EVENTS);
            if (res?.data?.s === 1) {
                const r = res.data.r || {};
                setEvents((r.events || []).map((e, i) => ({ ...e, rowid: i + 1 })));
                setBadges(r.badges || []);
                setCosmetics(r.cosmetics || []);
            } else {
                toast.error(res?.data?.m || "Failed to load events");
            }
        } catch (e) {
            toast.error(e?.response?.data?.m || e.message || "Failed to load events");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(EMPTY_FORM); setDialogOpen(true); };
    const openEdit = (ev) => {
        setForm({
            id: ev.id,
            code: ev.code || "",
            name: ev.name || "",
            partner: ev.partner || "",
            venue: ev.venue || "",
            description: ev.description || "",
            event_date: ev.event_date ? String(ev.event_date).slice(0, 10) : "",
            starts_at: isoToLocal(ev.starts_at),
            ends_at: isoToLocal(ev.ends_at),
            max_redemptions: ev.max_redemptions ?? "",
            badge_id: ev.badge_id ?? "",
            cosmetic_id: ev.cosmetic_id ?? "",
            tp_amount: ev.tp_amount ?? 0,
        });
        setDialogOpen(true);
    };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        if (!form.code.trim() || !form.name.trim() || !form.starts_at || !form.ends_at) {
            toast.error("Code, name, start and end are required");
            return;
        }
        setSaving(true);
        try {
            const body = {
                code: form.code.trim(),
                name: form.name.trim(),
                partner: form.partner,
                venue: form.venue,
                description: form.description,
                event_date: form.event_date,
                starts_at: localToIso(form.starts_at),
                ends_at: localToIso(form.ends_at),
                max_redemptions: form.max_redemptions === "" ? "" : String(form.max_redemptions),
                badge_id: form.badge_id === "" ? "" : String(form.badge_id),
                cosmetic_id: form.cosmetic_id === "" ? "" : String(form.cosmetic_id),
                tp_amount: String(form.tp_amount || 0),
            };
            const res = form.id
                ? await AxiosInstance.post(`${ApiList.ADMIN_EVENTS}/${form.id}`, body)
                : await AxiosInstance.post(ApiList.ADMIN_EVENTS, body);
            if (res?.data?.s === 1) {
                toast.success(res.data.m || "Saved");
                setDialogOpen(false);
                await load();
            } else {
                toast.error(res?.data?.m || "Save failed");
            }
        } catch (e) {
            toast.error(e?.response?.data?.m || e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const setStatus = async (ev, status) => {
        const verb = status === 1 ? "Go live" : status === -1 ? "Archive" : "Set to draft";
        const result = await Swal.fire({
            title: `${verb}: ${ev.name}?`,
            text: status === 1
                ? `Attendees will be able to redeem ${ev.code} inside the window.`
                : status === -1
                    ? "Redemptions and rewards already granted are kept."
                    : "The code stops working until it goes live again.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: verb,
        });
        if (!result.isConfirmed) return;
        try {
            const res = await AxiosInstance.post(`${ApiList.ADMIN_EVENTS}/${ev.id}/status`, { status });
            if (res?.data?.s === 1) {
                toast.success(res.data.m);
                await load();
            } else {
                toast.error(res?.data?.m || "Status change failed");
            }
        } catch (e) {
            toast.error(e?.response?.data?.m || e.message || "Status change failed");
        }
    };

    const columns = useMemo(() => [
        { field: "rowid", headerName: "#", width: 55, sortable: false },
        {
            field: "badge_image", headerName: "Medal", width: 70, sortable: false,
            renderCell: (p) => p.value
                ? <Avatar src={`${ApiList.IMG_URL}${p.value}`} variant="rounded" sx={{ width: 40, height: 40 }} />
                : <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: "#8883" }}><EventIcon sx={{ color: "#666" }} /></Avatar>,
        },
        {
            field: "cosmetic_thumb", headerName: "Item", width: 70, sortable: false,
            renderCell: (p) => p.value
                ? <Avatar src={`${ApiList.IMG_URL}${p.value}`} variant="rounded" sx={{ width: 40, height: 40, bgcolor: "#fff" }} />
                : <Typography variant="caption" color="text.secondary">—</Typography>,
        },
        { field: "name", headerName: "Event", flex: 1.2, minWidth: 180 },
        {
            field: "code", headerName: "Code", width: 140,
            renderCell: (p) => <Typography sx={{ fontFamily: "monospace", fontWeight: 700 }}>{p.value}</Typography>,
        },
        { field: "event_date", headerName: "Date", width: 110, renderCell: (p) => (p.value ? String(p.value).slice(0, 10) : "—") },
        {
            field: "starts_at", headerName: "Window (local)", width: 260, sortable: false,
            renderCell: (p) => <Typography variant="caption">{fmtLocal(p.row.starts_at)} → {fmtLocal(p.row.ends_at)}</Typography>,
        },
        {
            field: "redemption_count", headerName: "Redeemed", width: 110,
            renderCell: (p) => `${p.value ?? 0}${p.row.max_redemptions ? ` / ${p.row.max_redemptions}` : ""}`,
        },
        { field: "tp_amount", headerName: "TP", width: 70 },
        {
            field: "status", headerName: "Status", width: 100, sortable: false,
            renderCell: (p) => {
                const meta = STATUS_META[String(p.value)] || STATUS_META[0];
                return <Chip size="small" label={meta.label} sx={{ bgcolor: meta.bg, fontWeight: 600 }} />;
            },
        },
        {
            field: "actions", headerName: "", width: 150, sortable: false,
            renderCell: (p) => {
                const ev = p.row;
                return (
                    <Box>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(ev)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        {ev.status !== 1 && (
                            <Tooltip title="Go live"><IconButton size="small" color="success" onClick={() => setStatus(ev, 1)}><RocketLaunchIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {ev.status === 1 && (
                            <Tooltip title="Set to draft"><IconButton size="small" onClick={() => setStatus(ev, 0)}><UnpublishedIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {ev.status !== -1 && (
                            <Tooltip title="Archive"><IconButton size="small" color="error" onClick={() => setStatus(ev, -1)}><ArchiveIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                    </Box>
                );
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [events]);

    const qrUrl = form.code ? `https://treatwalk.com/download?ref=${form.code.trim().toLowerCase()}` : "";

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Events</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Event check-in codes. Make the medal in Badge Manager (criteria "event_checkin", premium off) and the item in Closet Manager (acquisition "Event exclusive") first.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New event</Button>
            </Box>

            <Box sx={{ width: "100%", height: "calc(100vh - 120px)", p: 2 }}>
                <TableContainer
                    columns={columns}
                    rows={events}
                    loading={loading}
                    page={page}
                    handlePageChange={setPage}
                    totalData={events.length}
                    pageSize={pageSize}
                />
            </Box>

            <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{form.id ? "Edit event" : "New event"}</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="normal" label="Name" value={form.name} onChange={set("name")} />
                    <TextField
                        fullWidth margin="normal" label="Code (what attendees type)" value={form.code}
                        onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }))}
                        inputProps={{ style: { fontFamily: "monospace", letterSpacing: 2 }, maxLength: 32 }}
                        helperText="4–32 letters/digits. Can't change once anyone has redeemed."
                    />
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField fullWidth margin="normal" label="Partner" value={form.partner} onChange={set("partner")} />
                        <TextField fullWidth margin="normal" label="Venue" value={form.venue} onChange={set("venue")} />
                    </Box>
                    <TextField fullWidth margin="normal" label="Description" value={form.description} onChange={set("description")} multiline minRows={2} inputProps={{ maxLength: 500 }} />
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField fullWidth margin="normal" type="date" label="Event date (display)" value={form.event_date} onChange={set("event_date")} InputLabelProps={{ shrink: true }} />
                        <TextField fullWidth margin="normal" type="number" label="Cap (blank = none)" value={form.max_redemptions} onChange={set("max_redemptions")} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField fullWidth margin="normal" type="datetime-local" label="Redeemable from" value={form.starts_at} onChange={set("starts_at")} InputLabelProps={{ shrink: true }} />
                        <TextField fullWidth margin="normal" type="datetime-local" label="Redeemable until" value={form.ends_at} onChange={set("ends_at")} InputLabelProps={{ shrink: true }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Times are your local time. Open the day before; keep it open ~2 weeks after so late installs still get the rewards.
                    </Typography>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Medal</InputLabel>
                        <Select label="Medal" value={form.badge_id} onChange={set("badge_id")}>
                            <MenuItem value="">None</MenuItem>
                            {badges.map((b) => (
                                <MenuItem key={b.id} value={b.id}>
                                    {b.name} {b.status !== 1 ? "(not live)" : ""}{b.is_premium ? " (premium — must be off)" : ""}{!b.image ? " (no art)" : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Doggo item</InputLabel>
                        <Select label="Doggo item" value={form.cosmetic_id} onChange={set("cosmetic_id")}>
                            <MenuItem value="">None</MenuItem>
                            {cosmetics.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                    {c.name} · {c.slot}{c.status !== 1 ? " (not live)" : ""}{!(c.layer_image && c.thumb_image) ? " (no art)" : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField fullWidth margin="normal" type="number" label="TreatPoints" value={form.tp_amount} onChange={set("tp_amount")} inputProps={{ min: 0, max: 5000 }} />
                    {qrUrl && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: "#f5f5f0", borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">QR / signage link</Typography>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 13 }}>{qrUrl}</Typography>
                            <Typography variant="caption" color="text.secondary">Print under it: "Enter code {form.code} in the app".</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EventManager;
